/**
 * Video URL Resolver
 * Provides a unified interface for resolving browser-playable video URLs.
 */

import type { components } from '@startech/openapi'

/** API `File` fields needed here — derive from `@startech/openapi`, never duplicate schema shapes */
export type VideoFile = Pick<components['schemas']['File'], 'id' | 'playback'>

/**
 * Normalized Mux playback subset for mux-player-react (stream + optional poster / strip thumbnail).
 * Client-only composition from API `playback` — wire shape stays on OpenAPI `Playback`.
 */
export type MuxPlaybackEmbed = {
  playbackId: string
  thumbnailUrl?: string
  tokens?: {
    playback?: string
    thumbnail?: string
    storyboard?: string
  }
}

export interface ResolvedVideoUrls {
  /** Primary stream/video URL */
  streamUrl: string
  /** Function to get thumbnail at a specific time (seconds) */
  getThumbnailUrl: (timeSeconds: number, options?: { width?: number; height?: number }) => string
  /** Whether this is an HLS stream or a direct file URL */
  isHls: boolean
  mux?: MuxPlaybackEmbed
}

/**
 * Returns true when the URI can be used directly by the browser for playback.
 * This excludes non-HTTP analysis/storage URIs, which are valid for backend processing but not for frontend playback.
 */
export const isPlayableVideoUrl = (uri?: string | null): uri is string => {
  if (!uri || !/^https?:\/\//.test(uri)) {
    return false
  }

  return /\.(mp4|m3u8)(?:$|\?)/i.test(uri)
}

export const isMuxPlaybackReady = (file?: VideoFile | null): boolean => {
  return Boolean(file?.playback?.status === 'ready' && file.playback.playbackId)
}

/**
 * Resolve video URLs from a file record
 * Uses the provided fallback URL when it is browser-playable.
 */
export const resolveVideoUrls = (file: VideoFile, fallbackUrl?: string): ResolvedVideoUrls | null => {
  if (isMuxPlaybackReady(file) && file.playback?.streamUrl && file.playback.playbackId) {
    return {
      streamUrl: file.playback.streamUrl,
      getThumbnailUrl: () => file.playback?.thumbnailUrl ?? '',
      isHls: true,
      mux: {
        playbackId: file.playback.playbackId,
        thumbnailUrl: file.playback.thumbnailUrl ?? undefined,
        tokens: file.playback.tokens
          ? {
              playback: file.playback.tokens.playback ?? undefined,
              thumbnail: file.playback.tokens.thumbnail ?? undefined,
              storyboard: file.playback.tokens.storyboard ?? undefined,
            }
          : undefined,
      },
    }
  }

  if (fallbackUrl) {
    const isHls = fallbackUrl.includes('.m3u8')

    return {
      streamUrl: fallbackUrl,
      getThumbnailUrl: () => '',
      isHls,
    }
  }

  return null
}
